/**
 * Logique pure de RÉORDONNANCEMENT (glisser-déposer de l'éditeur back-office).
 * Aucune dépendance réseau/DOM. La persistance écrira ensuite les `position` calculées.
 * Couvre les 4 niveaux : catégories, produits (dont déplacement entre catégories),
 * groupes d'options, valeurs. Tous manipulent une simple liste d'identifiants ordonnée.
 */

/**
 * Déplace `movingId` juste AVANT `beforeId` dans la liste.
 * `beforeId = null` → place l'élément en dernier. Robuste si un id est introuvable.
 */
export function moveInList(
  ids: string[],
  movingId: string,
  beforeId: string | null,
): string[] {
  const arr = ids.filter((i) => i !== movingId);
  if (beforeId == null) {
    arr.push(movingId);
    return arr;
  }
  const j = arr.indexOf(beforeId);
  if (j < 0) arr.push(movingId);
  else arr.splice(j, 0, movingId);
  return arr;
}

/** Transforme une liste ordonnée d'ids en couples {id, position} (0,1,2…) à persister. */
export function withPositions(ids: string[]): { id: string; position: number }[] {
  return ids.map((id, position) => ({ id, position }));
}

/**
 * Déplace un élément d'une liste source vers une liste cible (ex. produit changeant de
 * catégorie), inséré avant `beforeId` (ou en dernier si null). Retourne les 2 listes mises à jour.
 * Gère le cas « même liste » (réordonnancement interne).
 */
export function moveAcross(
  from: string[],
  to: string[],
  movingId: string,
  beforeId: string | null,
): { from: string[]; to: string[] } {
  const sameList = from === to || (from.includes(movingId) && to.includes(movingId) && from.length === to.length && from.every((v, i) => v === to[i]));
  if (sameList) {
    const reordered = moveInList(from, movingId, beforeId);
    return { from: reordered, to: reordered };
  }
  const nextFrom = from.filter((i) => i !== movingId);
  const nextTo = moveInList(
    to.includes(movingId) ? to : [...to, movingId],
    movingId,
    beforeId,
  );
  return { from: nextFrom, to: nextTo };
}
